import React, { useEffect, useState } from 'react';
import useBidirectionalScroll from '../hooks/useBidirectionalScroll';
import aboutus from '../assets/contact_hero.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faMapMarkerAlt, faPhoneAlt, faEnvelope, faGlobe } from '@fortawesome/free-solid-svg-icons';

export default function ContactPage({ navigateTo }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [status, setStatus] = useState(null);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useBidirectionalScroll('.contact-page-container', 120);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const waNumber = '919723962464';
    const text = `*New Contact Inquiry*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone || 'N/A'}\n*Company:* ${formData.company || 'N/A'}\n\n*Message:*\n${formData.message}`;
    const encodedText = encodeURIComponent(text);

    window.open(`https://wa.me/${waNumber}?text=${encodedText}`, '_blank', 'noopener,noreferrer');

    setStatus('success');
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    setTimeout(() => setStatus(null), 8000);
  };

  return (
    <div
      className="contact-page-container"
      style={{
        minHeight: '100vh', background: 'var(--bg-primary)'
      }}
    >
      <section className="hero" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* 3D Interactive Parallax Background Typography */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <div style={{
            fontSize: 'min(20vw, 250px)',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px rgba(10, 102, 255, 0.08)',
            letterSpacing: '10px',
            transform: `translate(${mousePos.x * 50}px, ${mousePos.y * 50}px)`,
            transition: 'transform 0.1s ease-out',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            CONNECT
          </div>
        </div>

        {/* Ambient Grid Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(10, 102, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(10, 102, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`,
          transition: 'transform 0.15s ease-out',
        }}></div>

        <div className="hero-background-slider" style={{ zIndex: 2, transform: `translate(${mousePos.x * -30}px, ${mousePos.y * -30}px)`, transition: 'transform 0.2s ease-out' }}>
          <div className="full-slide active">
            <div className="bg-img-container" style={{ position: 'absolute', inset: 0 }}>
              <img src={aboutus} alt="Contact Header" className="bg-img" style={{ objectPosition: 'right center' }} />
            </div>
            <div className="bg-overlay"></div>
          </div>
        </div>

        <div className="hero-content" style={{ zIndex: 10 }}>
          <button className="outline-button" style={{ marginBottom: '2rem' }} onClick={() => navigateTo('home')}>
            &#8592; Back to Home
          </button>
          <h1 className="hero-title">Contact Us</h1>
          <p className="hero-description">
            <strong style={{ color: "var(--accent-color)" }}>Get In Touch</strong><br /><br />
            Connect with our team for technical support, machinery inquiries, and comprehensive service.
          </p>
        </div>
      </section>

      <section
        className="contact-content-grid animate-hidden delay-100"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '4rem',
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '6rem 2rem'
        }}
      >
        {/* LEFT: CONTACT FORM */}
        <div className="contact-form-side">
          <h3 className="sub-section-title" style={{ marginBottom: '2rem' }}>Send us a message</h3>
          <form className="custom-contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input type="text" name="company" value={formData.company} onChange={handleChange} />
              </div>
            </div>

            <div className="form-group">
              <label>Message *</label>
              <textarea rows="6" name="message" value={formData.message} onChange={handleChange} required />
            </div>

            <div style={{ marginTop: '2rem' }}>
              <button type="submit" className="primary-button" style={{ width: '100%' }} disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending...' : 'Submit Inquiry'}
              </button>
              {status === 'success' && (
                <p style={{ color: 'green', marginTop: '1rem', textAlign: 'center' }}>
                  Inquiry sent successfully!
                </p>
              )}
            </div>
          </form>
        </div>

        {/* RIGHT: CONTACT INFO SIDE */}
        <div className="contact-info-side" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <h3 className="sub-section-title" style={{ marginBottom: '0.5rem' }}>Find Us</h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1.5rem'
          }}>
            {/* CARD 1 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
              transition: 'transform 0.3s ease'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                backgroundColor: 'rgba(10, 102, 255, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: 'var(--accent-color)', fontSize: '1.3rem' }} />
              </div>
              <h4 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--bg-dark)' }}>Corporate Facility</h4>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                Sahjanand Ind Area, St. 1,<br />
                Near Murlidhar Weigh Bridge,<br />
                Kothariya Ring Rd, Rajkot
              </p>
            </div>

            {/* CARD 2 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
              transition: 'transform 0.3s ease'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                backgroundColor: 'rgba(10, 102, 255, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <FontAwesomeIcon icon={faPhoneAlt} style={{ color: 'var(--accent-color)', fontSize: '1.3rem', transform: 'scaleX(-1)' }} />
              </div>
              <h4 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--bg-dark)' }}>Direct Lines</h4>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                +91 97239 62464<br />
                +91 96018 42434
              </p>
            </div>

            {/* CARD 3 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
              transition: 'transform 0.3s ease'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                backgroundColor: 'rgba(10, 102, 255, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ color: 'var(--accent-color)', fontSize: '1.3rem' }} />
              </div>
              <h4 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--bg-dark)' }}>Email Support</h4>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-muted)', lineHeight: '1.6', wordBreak: 'break-all' }}>
                nd.tools@yahoo.in
              </p>
            </div>

            {/* CARD 4 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
              transition: 'transform 0.3s ease'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                backgroundColor: 'rgba(10, 102, 255, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <FontAwesomeIcon icon={faGlobe} style={{ color: 'var(--accent-color)', fontSize: '1.3rem' }} />
              </div>
              <h4 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--bg-dark)' }}>Connect</h4>
              <div className="social-links" style={{ display: 'flex', gap: '1.2rem', marginTop: '0.8rem' }}>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faFacebook} style={{ fontSize: '24px', color: '#1877F2' }} />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faInstagram} style={{ fontSize: '24px', color: '#E4405F' }} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM: FULL-WIDTH MAP */}
      <section className="contact-map-section animate-hidden" style={{ paddingBottom: '8rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', height: '500px', padding: '0 2rem' }}>
          <iframe
            title="Rajkot Location"
            src="https://maps.google.com/maps?q=Kothariya%20Ring%20Road,%20Rajkot&t=&z=13&ie=UTF8&iwloc=&output=embed"
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '16px',
              border: '1px solid var(--border-color)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
            }}
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}
